import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LayoutSettings } from "../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../widgets/EntityHeader/EntityHeader";
import { Box } from "../../../../shared/Box/Box";
import { http } from "../../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../../consts/api";
import { uiNotification } from "../../../../services/UINotificationService";
import styles from "./wellsync.module.scss";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import { WellSyncLegacyConfig } from "./WellSyncLegacyConfig";
import { WellSyncTeammateSelect } from "./WellSyncTeammateSelect";

const STEP_LABELS = ["Location", "Users", "Providers", "Shipping", "Complete"];

function freshDraft(clinicId, locationName) {
  return {
    clinicId: clinicId ? String(clinicId) : "",
    locationName: locationName || "",
    users: [{ userId: "", role: "admin" }],
    providers: [
      {
        userId: "",
        npi: "",
        deaRegs: [],
      },
    ],
    shipping: { twoDay: true, overnight: true },
    enabled: true,
  };
}

function clinicNeedsApiCredentials(c) {
  if (!c) return false;
  if (c.hasClinicApiKey === false || c.hasSigningKey === false) {
    return true;
  }
  if (c.hasClinicApiKey == null && c.hasSigningKey == null) {
    return !c.hasCredentials;
  }
  return false;
}

function licenseExpiresAtToIso(dateStr) {
  if (!dateStr) return "";
  if (/T\d/.test(dateStr)) return dateStr;
  const d = new Date(`${dateStr}T12:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

function licenseExpiresAtToDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return String(iso).slice(0, 10);
  }
  return d.toISOString().slice(0, 10);
}

function licensesFromStoredPrescriber(p) {
  if (!Array.isArray(p?.licenses) || p.licenses.length === 0) {
    return undefined;
  }
  return p.licenses.map((l, idx) => ({
    state: l.state || "",
    licenseNumber: l.licenseNumber || "",
    expiresAt: licenseExpiresAtToDateInput(l.expiresAt || ""),
    isPrimary: idx === 0 ? true : !!l.isPrimary,
    wellsyncLicenseId: l.wellsyncLicenseId || "",
  }));
}

function serializeProviderLicenses(licenses) {
  return (licenses || [])
    .filter((l) => l.state && l.licenseNumber && l.expiresAt)
    .map((l, idx) => ({
      state: String(l.state).toUpperCase().slice(0, 2),
      licenseNumber: String(l.licenseNumber).trim(),
      expiresAt: licenseExpiresAtToIso(l.expiresAt),
      isPrimary: idx === 0 ? true : !!l.isPrimary,
      ...(l.wellsyncLicenseId
        ? { wellsyncLicenseId: String(l.wellsyncLicenseId) }
        : {}),
    }));
}

function buildLicensesForPayload(provider, providerOptions) {
  const serialized = serializeProviderLicenses(provider.licenses || []);
  if (serialized.length > 0) {
    return serialized;
  }
  const teammate = (providerOptions || []).find(
    (t) => String(t.id) === String(provider.userId),
  );
  const state = (teammate?.state || "").toUpperCase().slice(0, 2);
  const licenseNumber = String(teammate?.stateLicense || "").trim();
  if (state.length === 2 && licenseNumber) {
    return [{ state, licenseNumber, isPrimary: true }];
  }
  return [];
}

function ensureDraftProvidersShape(providers) {
  return (providers || []).map((p) => ({
    ...p,
    deaRegs: Array.isArray(p.deaRegs) ? p.deaRegs : [],
  }));
}

function validateSetupProviders(providers) {
  const rows = providers || [];
  if (rows.length === 0) {
    return "Add at least one provider.";
  }
  for (let i = 0; i < rows.length; i++) {
    const p = rows[i];
    if (!p.userId) {
      return `Provider ${i + 1}: select a clinician.`;
    }
    if (!/^\d{10}$/.test(String(p.npi || "").trim())) {
      return `Provider ${i + 1}: enter a valid 10-digit NPI.`;
    }
  }
  return null;
}

function formatDeaSummary(deaRegs) {
  const states = (deaRegs || [])
    .map((d) => String(d.state || "").toUpperCase())
    .filter(Boolean);
  return states.length ? states.join(", ") : "";
}

function formatConnectedDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatClinicAddressOneLine(detail) {
  if (!detail) return "";
  const cityState = [detail.city, detail.state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, detail.zip].filter(Boolean).join(" ");
  return [detail.address, cityStateZip]
    .filter((p) => p && String(p).trim())
    .join(", ");
}

function formatShippingSummary(shipping) {
  const parts = [];
  if (shipping?.twoDay) {
    parts.push("2-Day (non-cold) — $29.99");
  }
  if (shipping?.overnight) {
    parts.push("Overnight — $25.00");
  }
  return parts.length ? parts.join(" · ") : "No method selected";
}

function toggleShippingOption(shipping, key) {
  const next = { ...shipping, [key]: !shipping[key] };
  if (!next.twoDay && !next.overnight) {
    return shipping;
  }
  return next;
}

function setupIdFor(config) {
  return config.setupId || config.id;
}

function clinicRecordToDetail(clinic) {
  if (!clinic) return null;
  const emailRaw = clinic.appointment_notification_emails || "";
  const email = String(emailRaw).split(",")[0]?.trim() || "";
  let address = clinic.address || "";
  if (clinic.suite_number) {
    address = address
      ? `${address}, ${clinic.suite_number}`
      : clinic.suite_number;
  }
  return {
    address,
    city: clinic.clinic_city || "",
    state: clinic.clinic_state || "",
    zip: clinic.clinic_zipcode || "",
    phone: clinic.contact_no || "",
    email,
  };
}

export function WellSyncOnboarding() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("dashboard");
  const [clinics, setClinics] = useState([]);
  const [configured, setConfigured] = useState([]);
  const [operationalUsers, setOperationalUsers] = useState([]);
  const [providerTeammates, setProviderTeammates] = useState([]);
  const [teammatesLoading, setTeammatesLoading] = useState(false);
  const [provisioningEnabled, setProvisioningEnabled] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [showLegacy, setShowLegacy] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [doneSteps, setDoneSteps] = useState(new Set());
  const [draft, setDraft] = useState(freshDraft());
  const [editingId, setEditingId] = useState(null);
  const [clinicPendingAction, setClinicPendingAction] = useState(null);
  const [addPrescriberConfigId, setAddPrescriberConfigId] = useState(null);
  const [addingPrescriber, setAddingPrescriber] = useState({
    userId: "",
    npi: "",
    deaRegs: [],
  });
  const [clinicDetail, setClinicDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, clinRes] = await Promise.all([
        http.get(HTTP_ENDPOINTS.wellsync.onboardingDashboard()),
        http.get(HTTP_ENDPOINTS.getClinics()),
      ]);
      const dash = dashRes.data?.data;
      const configuredList = Array.isArray(dash?.clinics) ? dash.clinics : [];
      setConfigured(configuredList);
      setProvisioningEnabled(!!dash?.provisioningEnabled);
      const raw = clinRes.data?.data;
      const clinicList = Array.isArray(raw) ? raw : raw?.data || [];
      const clinicsArr = Array.isArray(clinicList) ? clinicList : [];
      setClinics(clinicsArr);
      if (configuredList.length === 0 && clinicsArr.length > 0) {
        const first = clinicsArr[0];
        setMode("wizard");
        setDraft(freshDraft(first.id, first.clinic_name || first.name || ""));
      }
      setOperationalUsers([]);
      setProviderTeammates([]);
    } catch (e) {
      uiNotification.error("Failed to load WellSync settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const clinicOptions = useMemo(() => {
    const used = new Set(configured.map((c) => Number(c.clinicId)));
    return clinics.filter((c) => c.id != null && !used.has(Number(c.id)));
  }, [clinics, configured]);

  const preloadClinicTeammates = useCallback(async (clinicId) => {
    if (!clinicId) {
      setOperationalUsers([]);
      setProviderTeammates([]);
      setTeammatesLoading(false);
      return;
    }
    setTeammatesLoading(true);
    setOperationalUsers([]);
    setProviderTeammates([]);
    try {
      const opRes = await http.get(
        HTTP_ENDPOINTS.wellsync.onboardingTeammates("operational", clinicId),
      );
      setOperationalUsers(
        Array.isArray(opRes.data?.data) ? opRes.data.data : [],
      );
      const provRes = await http.get(
        HTTP_ENDPOINTS.wellsync.onboardingTeammates("providers", clinicId),
      );
      setProviderTeammates(
        Array.isArray(provRes.data?.data) ? provRes.data.data : [],
      );
    } catch {
      setOperationalUsers([]);
      setProviderTeammates([]);
    } finally {
      setTeammatesLoading(false);
    }
  }, []);

  useEffect(() => {
    void preloadClinicTeammates(draft.clinicId);
  }, [draft.clinicId, preloadClinicTeammates]);

  useEffect(() => {
    if (!draft.clinicId || operationalUsers.length === 0) {
      return;
    }
    const allowed = new Set(operationalUsers.map((u) => String(u.id)));
    setDraft((d) => {
      if (String(d.clinicId) !== String(draft.clinicId)) {
        return d;
      }
      let changed = false;
      const users = d.users.map((u) => {
        if (u.userId && !allowed.has(String(u.userId))) {
          changed = true;
          return { ...u, userId: "", role: "admin" };
        }
        return u;
      });
      return changed ? { ...d, users } : d;
    });
  }, [operationalUsers, draft.clinicId]);

  useEffect(() => {
    if (!draft.clinicId || providerTeammates.length === 0) {
      return;
    }
    const allowed = new Set(providerTeammates.map((p) => String(p.id)));
    setDraft((d) => {
      if (String(d.clinicId) !== String(draft.clinicId)) {
        return d;
      }
      let changed = false;
      const providers = d.providers.map((p) => {
        if (p.userId && !allowed.has(String(p.userId))) {
          changed = true;
          return { ...p, userId: "", npi: "" };
        }
        return p;
      });
      return changed ? { ...d, providers } : d;
    });
  }, [providerTeammates, draft.clinicId]);

  useEffect(() => {
    if (!addPrescriberConfigId) {
      return;
    }
    const config = configured.find((c) => c.id === addPrescriberConfigId);
    if (
      config?.clinicId &&
      String(config.clinicId) !== String(draft.clinicId)
    ) {
      void preloadClinicTeammates(config.clinicId);
    }
  }, [
    addPrescriberConfigId,
    configured,
    draft.clinicId,
    preloadClinicTeammates,
  ]);

  const providerOptions = useMemo(() => providerTeammates, [providerTeammates]);

  const loadClinicDetail = useCallback(async (clinicId, fallbackClinic) => {
    if (!clinicId) {
      setClinicDetail(null);
      return;
    }
    const fallback = clinicRecordToDetail(fallbackClinic);
    if (fallback) {
      setClinicDetail(fallback);
    }
    try {
      const res = await http.get(
        HTTP_ENDPOINTS.wellsync.onboardingClinic(clinicId),
      );
      const detail = res.data?.data;
      if (detail) {
        setClinicDetail(detail);
      }
    } catch {
      if (!fallback) {
        setClinicDetail(null);
      }
    }
  }, []);

  useEffect(() => {
    if (!draft.clinicId) {
      setClinicDetail(null);
      return;
    }
    const selected = clinics.find(
      (c) => String(c.id) === String(draft.clinicId),
    );
    void loadClinicDetail(draft.clinicId, selected);
  }, [draft.clinicId, clinics, loadClinicDetail]);

  const canAdvance = useMemo(() => {
    if (currentStep === 0) {
      return !!draft.clinicId && !!draft.locationName.trim();
    }
    if (currentStep === 1) {
      return draft.users.length > 0 && draft.users.every((u) => u.userId);
    }
    if (currentStep === 2) {
      return (
        draft.providers.length > 0 &&
        draft.providers.every((p) => p.userId && /^\d{10}$/.test(p.npi.trim()))
      );
    }
    if (currentStep === 3) {
      return draft.shipping.twoDay || draft.shipping.overnight;
    }
    if (currentStep === 4) {
      return !validateSetupProviders(draft.providers);
    }
    return true;
  }, [currentStep, draft]);

  const setupSummary = useMemo(() => {
    const usersList = draft.users
      .filter((u) => u.userId)
      .map((u) => {
        const user = operationalUsers.find(
          (t) => String(t.id) === String(u.userId),
        );
        const role = u.role
          ? u.role.charAt(0).toUpperCase() + u.role.slice(1)
          : "Admin";
        return user ? `${user.name} — ${role}` : null;
      })
      .filter(Boolean);

    const providersList = draft.providers
      .filter((p) => p.userId)
      .map((p) => {
        const prov = providerOptions.find(
          (t) => String(t.id) === String(p.userId),
        );
        const name = prov?.name || "Unnamed provider";
        const deaStates = formatDeaSummary(p.deaRegs);
        const deaText = deaStates ? ` · DEA: ${deaStates}` : "";
        return `${name} — NPI ${p.npi || "—"}${deaText}`;
      });

    return {
      locationName: draft.locationName,
      locationAddress: formatClinicAddressOneLine(clinicDetail),
      users: usersList,
      providers: providersList,
      shipping: formatShippingSummary(draft.shipping),
      credentialsNote: provisioningEnabled
        ? "API keys will be issued automatically on save."
        : "After save, add Clinic API key and Prescription signing key from the clinic card.",
    };
  }, [
    draft,
    operationalUsers,
    providerOptions,
    clinicDetail,
    provisioningEnabled,
  ]);

  const openWizard = (prefill) => {
    setMode("wizard");
    setCurrentStep(0);
    setDoneSteps(new Set());
    setEditingId(null);
    setDraft(prefill || freshDraft());
    setLastAction(null);
  };

  const openEdit = (config) => {
    setEditingId(config.setupId || config.id);
    setMode("wizard");
    setCurrentStep(0);
    setDoneSteps(new Set([0, 1, 2, 3, 4]));
    setDraft({
      clinicId: String(config.clinicId),
      locationName: config.locationName || config.arClinicName,
      users: (config.users || []).map((u) => ({
        userId: String(u.arUserId || u.userId || ""),
        role: u.role || "admin",
      })),
      providers: (config.prescribers || []).map((p) => ({
        userId: String(p.arUserId || ""),
        npi: p.npi || "",
        deaRegs: (p.deaStates || []).map((st) => ({ number: "", state: st })),
        ...(licensesFromStoredPrescriber(p)
          ? { licenses: licensesFromStoredPrescriber(p) }
          : {}),
      })),
      shipping: {
        twoDay: config.shipping?.twoDay !== false,
        overnight: !!config.shipping?.overnight,
      },
      enabled: config.isEnabled !== false,
    });
    setLastAction(null);
  };

  const buildPayload = () => {
    const providers = ensureDraftProvidersShape(draft.providers).map((p) => {
      const licenses = buildLicensesForPayload(p, providerOptions);
      return {
        userId: Number(p.userId),
        npi: p.npi.trim(),
        deaRegs: (p.deaRegs || []).filter((d) => d.number && d.state),
        ...(licenses.length ? { licenses } : {}),
      };
    });

    return {
      clinicId: Number(draft.clinicId),
      locationName: draft.locationName.trim(),
      users: draft.users.map((u) => ({
        userId: Number(u.userId),
        role: u.role || "admin",
      })),
      providers,
      shipping: { ...draft.shipping },
      enabled: draft.enabled,
    };
  };

  const completeSetup = async () => {
    const providerError = validateSetupProviders(
      ensureDraftProvidersShape(draft.providers),
    );
    if (providerError) {
      uiNotification.error(providerError);
      setCurrentStep(2);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await http.put(
          HTTP_ENDPOINTS.wellsync.onboardingSetupUpdate(editingId),
          payload,
        );
        setLastAction({
          type: "updated",
          name: draft.locationName,
        });
      } else {
        await http.post(HTTP_ENDPOINTS.wellsync.onboardingSetup(), payload);
        setLastAction({
          type: "connected",
          name: draft.locationName,
        });
      }
      setEditingId(null);
      setMode("dashboard");
      await load();
    } catch (err) {
      const data = err.response && err.response.data;
      const detail =
        data && data.data && data.data.detail ? data.data.detail : null;
      const message =
        detail || (data && data.message) || "Failed to save WellSync setup";
      if (message.includes("licenses")) {
        setCurrentStep(2);
      }
      uiNotification.error(message);
    } finally {
      setSaving(false);
    }
  };

  const savePrescriber = async () => {
    if (
      !addPrescriberConfigId ||
      !addingPrescriber.userId ||
      !/^\d{10}$/.test(addingPrescriber.npi.trim())
    ) {
      return;
    }
    setSaving(true);
    try {
      const licenses = buildLicensesForPayload(
        addingPrescriber,
        providerOptions,
      );
      await http.post(
        HTTP_ENDPOINTS.wellsync.onboardingAddPrescriber(addPrescriberConfigId),
        {
          userId: Number(addingPrescriber.userId),
          npi: addingPrescriber.npi.trim(),
          deaRegs: addingPrescriber.deaRegs.filter((d) => d.number && d.state),
          ...(licenses.length ? { licenses } : {}),
        },
      );
      setAddPrescriberConfigId(null);
      setLastAction({ type: "updated", name: "" });
      await load();
      uiNotification.success("Prescriber added");
    } catch (err) {
      uiNotification.error(
        err.response?.data?.message || "Failed to add prescriber",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleDisable = async (config) => {
    const setupId = setupIdFor(config);
    if (!setupId || clinicPendingAction) {
      return;
    }
    const nextEnabled = !config.isEnabled;
    const label = config.locationName || config.arClinicName || "this clinic";
    const confirmMessage = nextEnabled
      ? `Enable WellSync for "${label}"?`
      : `Disable WellSync for "${label}"?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setClinicPendingAction({ id: setupId, type: "toggle" });
    try {
      await http.post(`wellsync/configs/${setupId}/toggle`, {
        isEnabled: nextEnabled,
      });
      await load();
      uiNotification.success(
        nextEnabled ? "Clinic enabled" : "Clinic disabled",
      );
    } catch {
      uiNotification.error("Failed to update clinic");
    } finally {
      setClinicPendingAction(null);
    }
  };

  const markBillingActive = async (config) => {
    const setupId = setupIdFor(config);
    if (!setupId || clinicPendingAction) {
      return;
    }
    setClinicPendingAction({ id: setupId, type: "billing" });
    try {
      await http.post(HTTP_ENDPOINTS.wellsync.onboardingBillingActive(setupId));
      await load();
      uiNotification.success("Billing marked active");
    } catch (err) {
      uiNotification.error(
        err.response?.data?.message || "Failed to update billing status",
      );
    } finally {
      setClinicPendingAction(null);
    }
  };

  const isClinicActionPending = useCallback(
    (config, type) => {
      const id = setupIdFor(config);
      return (
        clinicPendingAction != null &&
        clinicPendingAction.id === id &&
        clinicPendingAction.type === type
      );
    },
    [clinicPendingAction],
  );

  const isClinicBusy = useCallback(
    (config) => {
      const id = setupIdFor(config);
      return clinicPendingAction != null && clinicPendingAction.id === id;
    },
    [clinicPendingAction],
  );

  const deleteConfig = async (config) => {
    const setupId = setupIdFor(config);
    if (!setupId || clinicPendingAction) {
      return;
    }
    if (
      !window.confirm(
        `Remove WellSync connection for ${
          config.locationName || config.arClinicName
        }?`,
      )
    ) {
      return;
    }
    setClinicPendingAction({ id: setupId, type: "delete" });
    try {
      await http.delete(HTTP_ENDPOINTS.wellsync.onboardingSetupDelete(setupId));
      await load();
      uiNotification.success("Clinic connection removed");
    } catch (err) {
      if (err.response?.status === 404) {
        await load();
        uiNotification.success("Clinic connection removed");
        return;
      }
      uiNotification.error(err.response?.data?.message || "Failed to delete");
    } finally {
      setClinicPendingAction(null);
    }
  };

  const copyFromFirst = (field) => {
    const first = configured[0];
    if (!first) return;
    if (field === "users" && first.users) {
      setDraft((d) => ({
        ...d,
        users: first.users.map((u) => ({
          userId: String(u.arUserId || u.userId || ""),
          role: u.role || "admin",
        })),
      }));
    }
    if (field === "providers" && first.prescribers) {
      setDraft((d) => ({
        ...d,
        providers: first.prescribers.map((p) => ({
          userId: String(p.arUserId || ""),
          npi: p.npi || "",
          deaRegs: [],
          ...(licensesFromStoredPrescriber(p)
            ? { licenses: licensesFromStoredPrescriber(p) }
            : {}),
        })),
      }));
    }
    if (field === "shipping" && first.shipping) {
      setDraft((d) => ({
        ...d,
        shipping: { ...first.shipping },
      }));
    }
  };

  const wizardBody = (
    <>
      <div className={styles.stepper}>
        {STEP_LABELS.map((label, i) => {
          let cls = "";
          if (doneSteps.has(i)) cls = styles.stepDone;
          else if (i === currentStep) cls = styles.stepCurrent;
          return (
            <button
              key={label}
              type="button"
              className={`${styles.step} ${cls}`}
              onClick={() => {
                if (doneSteps.has(i) || i <= currentStep) setCurrentStep(i);
              }}
            >
              <span className={styles.stepCircle}>
                {doneSteps.has(i) ? "✓" : i + 1}
              </span>
              <span className={styles.stepLabel}>{label}</span>
            </button>
          );
        })}
      </div>

      {currentStep === 0 && (
        <div className={styles.wizardStep}>
          <p className={styles.meta}>Step 1 of 5</p>
          <h2 className={styles.stepTitle}>Choose the location</h2>
          <p className={styles.stepDesc}>
            Pick the Aesthetic Record clinic to connect. We&apos;ll use its
            address, phone, and email as the location of record in WellSync.
          </p>
          <div className={styles.wizardForm}>
            <div className={styles.wizardField}>
              <span className={styles.fieldLabel}>AR Clinic</span>
              <select
                className={styles.wizardSelect}
                value={draft.clinicId}
                disabled={!!editingId}
                onChange={(e) => {
                  const id = e.target.value;
                  const c = clinics.find((x) => String(x.id) === id);
                  setClinicDetail(clinicRecordToDetail(c));
                  setDraft((d) => ({
                    ...d,
                    clinicId: id,
                    locationName: c?.clinic_name || c?.name || d.locationName,
                    users: [{ userId: "", role: "admin" }],
                    providers: [
                      {
                        userId: "",
                        npi: "",
                        deaRegs: [],
                      },
                    ],
                  }));
                }}
              >
                <option value="">Select clinic…</option>
                {(editingId
                  ? clinics.filter((c) => String(c.id) === draft.clinicId)
                  : clinicOptions
                ).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.clinic_name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.wizardField}>
              <span className={styles.fieldLabel}>
                Location name in WellSync
              </span>
              <input
                className={styles.wizardInput}
                value={draft.locationName}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, locationName: e.target.value }))
                }
              />
            </div>
            {draft.clinicId ? (
              <>
                <div className={styles.wizardField}>
                  <span className={styles.fieldLabel}>
                    Address{" "}
                    <span className={styles.fieldLabelOpt}>
                      (from AR clinic record)
                    </span>
                  </span>
                  <div className={styles.readonly}>
                    {formatClinicAddressOneLine(clinicDetail) || "—"}
                  </div>
                </div>
                <div className={styles.row2}>
                  <div className={styles.wizardField}>
                    <span className={styles.fieldLabel}>Phone</span>
                    <div className={styles.readonly}>
                      {clinicDetail?.phone || "—"}
                    </div>
                  </div>
                  <div className={styles.wizardField}>
                    <span className={styles.fieldLabel}>Email</span>
                    <div className={styles.readonly}>
                      {clinicDetail?.email || "—"}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            {draft.clinicId && teammatesLoading ? (
              <p className={styles.teammatesLoadingHint}>
                Loading operational users and providers for this clinic…
              </p>
            ) : null}
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div className={styles.wizardStep}>
          <p className={styles.meta}>Step 2 of 5</p>
          <h2 className={styles.stepTitle}>Add operational users</h2>
          <p className={styles.stepDesc}>
            Non-clinician users who&apos;ll access this location in WellSync.
            All users are added with the Admin role.
          </p>
          {configured.length > 0 && !editingId ? (
            <div className={styles.infoCta}>
              <span>Already set up users for another clinic?</span>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => copyFromFirst("users")}
              >
                Copy from existing clinic
              </button>
            </div>
          ) : null}
          {teammatesLoading ? (
            <p className={styles.teammatesLoadingHint}>
              Loading users for this clinic…
            </p>
          ) : null}
          {draft.users.map((u, i) => {
            const selectedUser = operationalUsers.find(
              (t) => String(t.id) === String(u.userId),
            );
            const otherUserIds = draft.users
              .map((row, j) => (j !== i ? row.userId : ""))
              .filter(Boolean);
            return (
              <div key={i} className={styles.itemBox}>
                {draft.users.length > 1 ? (
                  <button
                    type="button"
                    className={styles.itemRm}
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        users: d.users.filter((_, j) => j !== i),
                      }))
                    }
                  >
                    ×
                  </button>
                ) : null}
                <div className={styles.row2}>
                  <div className={styles.wizardField}>
                    <span className={styles.fieldLabel}>User</span>
                    <WellSyncTeammateSelect
                      inputId={`wellsync-op-user-${i}`}
                      teammates={operationalUsers}
                      value={u.userId}
                      isLoading={teammatesLoading}
                      placeholder="Search AR teammates…"
                      variant="operational"
                      excludeUserIds={otherUserIds}
                      onChange={(v) => {
                        setDraft((d) => {
                          const users = [...d.users];
                          users[i] = { ...users[i], userId: v, role: "admin" };
                          return { ...d, users };
                        });
                      }}
                    />
                  </div>
                  <div className={styles.wizardField}>
                    <span className={styles.fieldLabel}>Role</span>
                    <select
                      className={styles.wizardSelect}
                      value={u.role || "admin"}
                      disabled
                    >
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                {selectedUser?.email ? (
                  <p className={styles.userEmailHint}>
                    Email: {selectedUser.email}
                  </p>
                ) : null}
              </div>
            );
          })}
          <p className={styles.stepHelp}>
            Don&apos;t see who you need? Add them to AR Teammates first, then
            return here.
          </p>
          <button
            type="button"
            className={styles.addLink}
            onClick={() =>
              setDraft((d) => ({
                ...d,
                users: [...d.users, { userId: "", role: "admin" }],
              }))
            }
          >
            + Add another user
          </button>
        </div>
      )}

      {currentStep === 2 && (
        <div className={styles.wizardStep}>
          <p className={styles.meta}>Step 3 of 5</p>
          <h2 className={styles.stepTitle}>Add providers (clinicians)</h2>
          <p className={styles.stepDesc}>
            Clinicians who will prescribe through this location. NPI is
            required. DEA registrations are required only for controlled
            substances and can be added one per state.
          </p>
          {configured.length > 0 && !editingId ? (
            <div className={styles.infoCta}>
              <span>Already set up providers for another clinic?</span>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => copyFromFirst("providers")}
              >
                Copy from existing clinic
              </button>
            </div>
          ) : null}
          {teammatesLoading ? (
            <p className={styles.teammatesLoadingHint}>
              Loading providers for this clinic…
            </p>
          ) : null}
          {draft.providers.map((p, i) => {
            const otherProviderIds = draft.providers
              .map((row, j) => (j !== i ? row.userId : ""))
              .filter(Boolean);
            return (
              <div key={i} className={styles.itemBox}>
                {draft.providers.length > 1 ? (
                  <button
                    type="button"
                    className={styles.itemRm}
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        providers: d.providers.filter((_, j) => j !== i),
                      }))
                    }
                  >
                    ×
                  </button>
                ) : null}
                <div className={styles.wizardField}>
                  <span className={styles.fieldLabel}>Provider</span>
                  <WellSyncTeammateSelect
                    inputId={`wellsync-provider-${i}`}
                    teammates={providerOptions}
                    value={p.userId}
                    isLoading={teammatesLoading}
                    placeholder="Select an AR provider…"
                    variant="provider"
                    excludeUserIds={otherProviderIds}
                    onChange={(v) => {
                      const prov = providerOptions.find(
                        (x) => String(x.id) === String(v),
                      );
                      setDraft((d) => {
                        const providers = [...d.providers];
                        providers[i] = {
                          ...providers[i],
                          userId: v,
                          npi: prov?.npi || providers[i].npi,
                        };
                        return { ...d, providers };
                      });
                    }}
                  />
                </div>
                <div className={styles.wizardField}>
                  <span className={styles.fieldLabel}>
                    NPI <span className={styles.fieldReq}>(required)</span>
                  </span>
                  <input
                    className={styles.input}
                    placeholder="10-digit NPI"
                    value={p.npi}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDraft((d) => {
                        const providers = [...d.providers];
                        providers[i] = { ...providers[i], npi: v };
                        return { ...d, providers };
                      });
                    }}
                  />
                </div>
                <div className={styles.deaSubhead}>
                  <span className={styles.deaSubheadLabel}>
                    DEA registrations
                  </span>
                  <span className={styles.deaSubheadHint}>
                    (required only for controlled substances)
                  </span>
                </div>
                {(p.deaRegs || []).length === 0 ? (
                  <p className={styles.deaEmptyHint}>
                    No DEA registrations yet. Add one only if this provider
                    prescribes controlled substances.
                  </p>
                ) : null}
                {(p.deaRegs || []).map((dea, j) => (
                  <div key={j} className={styles.deaRow}>
                    <input
                      className={styles.input}
                      placeholder="DEA number (e.g., AC1234567)"
                      value={dea.number}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraft((d) => {
                          const providers = [...d.providers];
                          const deaRegs = [...providers[i].deaRegs];
                          deaRegs[j] = { ...deaRegs[j], number: v };
                          providers[i] = { ...providers[i], deaRegs };
                          return { ...d, providers };
                        });
                      }}
                    />
                    <input
                      className={styles.input}
                      placeholder="State (e.g., TX)"
                      maxLength={2}
                      value={dea.state}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraft((d) => {
                          const providers = [...d.providers];
                          const deaRegs = [...providers[i].deaRegs];
                          deaRegs[j] = { ...deaRegs[j], state: v };
                          providers[i] = { ...providers[i], deaRegs };
                          return { ...d, providers };
                        });
                      }}
                    />
                    <button
                      type="button"
                      className={styles.deaRm}
                      onClick={() =>
                        setDraft((d) => {
                          const providers = [...d.providers];
                          providers[i] = {
                            ...providers[i],
                            deaRegs: providers[i].deaRegs.filter(
                              (_, k) => k !== j,
                            ),
                          };
                          return { ...d, providers };
                        })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className={styles.addLink}
                  onClick={() =>
                    setDraft((d) => {
                      const providers = [...d.providers];
                      providers[i] = {
                        ...providers[i],
                        deaRegs: [
                          ...(providers[i].deaRegs || []),
                          { number: "", state: "" },
                        ],
                      };
                      return { ...d, providers };
                    })
                  }
                >
                  + Add DEA
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className={styles.addLink}
            onClick={() =>
              setDraft((d) => ({
                ...d,
                providers: [
                  ...d.providers,
                  {
                    userId: "",
                    npi: "",
                    deaRegs: [],
                  },
                ],
              }))
            }
          >
            + Add provider
          </button>
        </div>
      )}

      {currentStep === 3 && (
        <div className={styles.wizardStep}>
          <p className={styles.meta}>Step 4 of 5</p>
          <h2 className={styles.stepTitle}>Set up shipping methods</h2>
          <p className={styles.stepDesc}>
            Select one or both options. Patients will only see the methods you
            enable here during the prescription checkout flow. 2-day cold
            shipping is temporarily unavailable at WellSync — only non-cold
            2-day is offered when enabled below.
          </p>
          <button
            type="button"
            className={`${styles.shipOpt} ${
              draft.shipping.twoDay ? styles.shipOptSel : ""
            }`}
            onClick={() =>
              setDraft((d) => ({
                ...d,
                shipping: toggleShippingOption(d.shipping, "twoDay"),
              }))
            }
          >
            <span className={styles.shipCheck} aria-hidden />
            <span className={styles.shipOptBody}>
              <span className={styles.shipOptTitle}>
                2-Day delivery (non-cold){" "}
                <span className={styles.shipOptPrice}>· $29.99</span>
              </span>
              <span className={styles.shipOptDesc}>
                Standard option. Arrives in 2 business days without cold-chain
                packaging.
              </span>
            </span>
          </button>
          <button
            type="button"
            className={`${styles.shipOpt} ${
              draft.shipping.overnight ? styles.shipOptSel : ""
            }`}
            onClick={() =>
              setDraft((d) => ({
                ...d,
                shipping: toggleShippingOption(d.shipping, "overnight"),
              }))
            }
          >
            <span className={styles.shipCheck} aria-hidden />
            <span className={styles.shipOptBody}>
              <span className={styles.shipOptTitle}>
                Overnight delivery{" "}
                <span className={styles.shipOptPrice}>· $25.00</span>
              </span>
              <span className={styles.shipOptDesc}>
                Premium option. Next business day delivery.
              </span>
            </span>
          </button>
        </div>
      )}

      {currentStep === 4 && (
        <div className={styles.wizardStep}>
          <p className={styles.meta}>Step 5 of 5</p>
          <h2 className={styles.stepTitle}>
            {editingId ? "Review your changes" : "Complete clinic setup"}
          </h2>
          <p className={styles.stepDesc}>
            {editingId
              ? "Review the changes below. Saving will update the clinic configuration immediately."
              : "Review the setup below. After completing, the admin user will receive an invitation to set up billing in the WellSync portal. Prescriptions cannot be sent until billing is set up."}
          </p>
          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Location</span>
              <span className={styles.summaryVal}>
                {setupSummary.locationName || "—"}
                {setupSummary.locationAddress ? (
                  <span className={styles.summarySub}>
                    {setupSummary.locationAddress}
                  </span>
                ) : null}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Users</span>
              <span className={styles.summaryVal}>
                {setupSummary.users.length ? (
                  setupSummary.users.map((line) => (
                    <span key={line} className={styles.summaryLine}>
                      {line}
                    </span>
                  ))
                ) : (
                  <span className={styles.summaryEmpty}>None added</span>
                )}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Providers</span>
              <span className={styles.summaryVal}>
                {setupSummary.providers.length ? (
                  setupSummary.providers.map((line) => (
                    <span key={line} className={styles.summaryLine}>
                      {line}
                    </span>
                  ))
                ) : (
                  <span className={styles.summaryEmpty}>None added</span>
                )}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Shipping</span>
              <span className={styles.summaryVal}>{setupSummary.shipping}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>API credentials</span>
              <span className={styles.summaryVal}>
                {setupSummary.credentialsNote}
              </span>
            </div>
          </div>
          {!editingId ? (
            <div className={styles.calloutWarn}>
              Before this location can send prescriptions, the admin user must
              complete billing setup in the WellSync portal. An invite will be
              sent when you complete setup.
            </div>
          ) : null}
          {!provisioningEnabled ? (
            <div className={styles.calloutInfo}>
              After save, add Clinic API key and Prescription signing key from
              the clinic card using Add API credentials.
            </div>
          ) : (
            <div className={styles.calloutInfo}>
              API keys will be issued automatically on save when
              auto-provisioning is enabled.
            </div>
          )}
          <label
            className={styles.enableRow}
            htmlFor="wellsync-enable-toggle"
            aria-label={
              editingId ? "Connection enabled" : "Enable connection now"
            }
          >
            <span className={styles.enableCopy}>
              <span className={styles.enableTitle}>
                {editingId ? "Connection enabled" : "Enable connection now"}
              </span>
              <span className={styles.enableDesc}>
                {editingId
                  ? "When disabled, this location is hidden from the prescribing flow."
                  : "Once enabled, this location is visible in the prescribing flow. You can change this later."}
              </span>
            </span>
            <input
              id="wellsync-enable-toggle"
              type="checkbox"
              className={styles.enableCheckbox}
              checked={draft.enabled}
              onChange={(e) =>
                setDraft((d) => ({ ...d, enabled: e.target.checked }))
              }
            />
          </label>
        </div>
      )}

      <div className={styles.wizardFoot}>
        {currentStep > 0 ? (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => setCurrentStep((s) => s - 1)}
          >
            ← Back
          </button>
        ) : configured.length > 0 ? (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => {
              setMode("dashboard");
              setEditingId(null);
            }}
          >
            Cancel
          </button>
        ) : (
          <span />
        )}
        {currentStep < 4 ? (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={!canAdvance}
            onClick={() => {
              setDoneSteps((s) => new Set([...s, currentStep]));
              setCurrentStep((c) => c + 1);
            }}
          >
            Continue →
          </button>
        ) : (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={saving}
            onClick={() => void completeSetup()}
          >
            {editingId ? "Save Changes" : "Complete Setup"}
          </button>
        )}
      </div>
    </>
  );

  if (showLegacy) {
    return (
      <WellSyncLegacyConfig
        onBack={() => {
          setShowLegacy(false);
          void load();
        }}
      />
    );
  }

  return (
    <LayoutSettings contentClassName={styles.root}>
      {mode !== "wizard" ? <EntityHeader title="WellSync Integration" /> : null}
      <Box>
        {loading ? (
          <CircularProgress />
        ) : mode === "wizard" ? (
          <div className={styles.wizardCard}>
            <div className={styles.wizardHead}>
              <h1 className={styles.wizardTitle}>
                {editingId
                  ? "Edit Clinic Connection"
                  : "Connect a Clinic to WellSync"}
              </h1>
              {!editingId ? (
                <p className={styles.wizardLead}>
                  Five guided steps to set up a clinic for prescription
                  workflows. You can add more clinics after setup.
                </p>
              ) : null}
            </div>
            {wizardBody}
          </div>
        ) : (
          <div className={styles.dashboardPanel}>
            <p className={styles.lead}>
              Manage clinics, providers, and shipping for prescription
              workflows.
            </p>
            {lastAction ? (
              <div className={styles.bannerSuccess}>
                {lastAction.type === "connected"
                  ? `Setup complete. ${lastAction.name} is connected. The admin user has been emailed instructions to complete billing setup in the WellSync portal.`
                  : `Changes saved. ${lastAction.name} has been updated.`}
              </div>
            ) : null}
            <h3 className={styles.dashboardSectionTitle}>Configured clinics</h3>
            {configured.length === 0 ? (
              <p className={styles.dashboardEmpty}>No clinics connected yet.</p>
            ) : (
              configured.map((c) => (
                <div key={c.id} className={styles.clinicCard}>
                  <div className={styles.ccHead}>
                    <div className={styles.ccInfo}>
                      <div className={styles.ccTitle}>
                        {c.locationName || c.arClinicName}
                      </div>
                      <div className={styles.ccMeta}>
                        {[c.city, c.state].filter(Boolean).join(", ")}
                        {c.city || c.state ? " · " : ""}
                        Connected {formatConnectedDate(c.connectedAt)}
                      </div>
                      <span
                        className={
                          c.billingStatus === "active"
                            ? styles.statusActive
                            : styles.statusWarn
                        }
                      >
                        {c.billingStatus === "active"
                          ? "✓ Active"
                          : "⚠ Awaiting billing setup"}
                      </span>
                      {c.hasClinicApiKey === false ||
                      (c.hasClinicApiKey == null && !c.hasCredentials) ? (
                        <div className={styles.provError}>
                          Clinic API key is still missing —{" "}
                          <button
                            type="button"
                            className={styles.inlineLink}
                            onClick={() => setShowLegacy(true)}
                          >
                            Add API credentials
                          </button>{" "}
                          from the WellSync portal to fully enable this
                          integration.
                        </div>
                      ) : null}
                      {c.hasSigningKey === false ||
                      (c.hasSigningKey == null && !c.hasCredentials) ? (
                        <div className={styles.provError}>
                          Prescription signing key is still missing —{" "}
                          <button
                            type="button"
                            className={styles.inlineLink}
                            onClick={() => setShowLegacy(true)}
                          >
                            Add API credentials
                          </button>{" "} to fully enable WellSync prescribing.
                        </div>
                      ) : null}
                      {c.provisioningLastError ? (
                        <div className={styles.provError}>
                          {c.provisioningLastError}
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.ccActions}>
                      {clinicNeedsApiCredentials(c) || !provisioningEnabled ? (
                        <button
                          type="button"
                          className={styles.btnOutline}
                          disabled={isClinicBusy(c)}
                          onClick={() => setShowLegacy(true)}
                        >
                          Add API credentials
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={styles.btnOutline}
                        disabled={isClinicBusy(c)}
                        onClick={() => {
                          setAddPrescriberConfigId(c.id);
                          setAddingPrescriber({
                            userId: "",
                            npi: "",
                            deaRegs: [],
                          });
                        }}
                      >
                        + Add Prescribers
                      </button>
                      <button
                        type="button"
                        className={styles.btnGhost}
                        disabled={isClinicBusy(c)}
                        onClick={() => openEdit(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={
                          c.isEnabled ? styles.btnDisable : styles.btnEnable
                        }
                        disabled={isClinicBusy(c)}
                        onClick={() => void toggleDisable(c)}
                      >
                        <span className={styles.btnWithSpinner}>
                          {isClinicActionPending(c, "toggle") ? (
                            <CircularProgress size="tiny" color="gray1" />
                          ) : null}
                          {isClinicActionPending(c, "toggle")
                            ? c.isEnabled
                              ? "Disabling…"
                              : "Enabling…"
                            : c.isEnabled
                            ? "Disable"
                            : "Enable"}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.btnDangerSoft}
                        disabled={isClinicBusy(c)}
                        onClick={() => void deleteConfig(c)}
                      >
                        <span className={styles.btnWithSpinner}>
                          {isClinicActionPending(c, "delete") ? (
                            <CircularProgress size="tiny" color="red" />
                          ) : null}
                          {isClinicActionPending(c, "delete")
                            ? "Deleting…"
                            : "Delete"}
                        </span>
                      </button>
                      {c.billingStatus !== "active" ? (
                        <button
                          type="button"
                          className={styles.btnGhost}
                          disabled={isClinicBusy(c)}
                          onClick={() => void markBillingActive(c)}
                        >
                          <span className={styles.btnWithSpinner}>
                            {isClinicActionPending(c, "billing") ? (
                              <CircularProgress size="tiny" color="gray1" />
                            ) : null}
                            {isClinicActionPending(c, "billing")
                              ? "Updating…"
                              : "Mark billing active"}
                          </span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.prescribers}>
                    <div className={styles.prescribersHead}>
                      <span>Prescribers</span>
                      <span>
                        {c.prescriberCount} total · {c.controlledDeaCount} with
                        controlled-substance DEA
                      </span>
                    </div>
                    {(c.prescribers || []).map((p) => (
                      <div key={p.arUserId} className={styles.prescriberRow}>
                        <span className={styles.prescriberName}>{p.name}</span>
                        <span>NPI {p.npi}</span>
                        <span
                          className={
                            p.deaStates?.length
                              ? styles.prescriberDea
                              : styles.prescriberDeaMuted
                          }
                        >
                          {p.deaStates?.length
                            ? p.deaStates.join(" ")
                            : "No controlled substances"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
            <button
              type="button"
              className={`${styles.btnPrimary} ${styles.btnBlock}`}
              disabled={!!clinicPendingAction}
              onClick={() => {
                const next = clinicOptions[0];
                openWizard(
                  freshDraft(next?.id, next?.clinic_name || next?.name || ""),
                );
              }}
            >
              + Connect Another Clinic
            </button>
            {!provisioningEnabled ? (
              <p className={styles.legacyLink}>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => setShowLegacy(true)}
                >
                  Add API credentials
                </button>
              </p>
            ) : null}
          </div>
        )}

        {addPrescriberConfigId ? (
          <div className={styles.modalBackdrop}>
            <div className={styles.modalMedium}>
              <h3>Add Prescriber</h3>
              <WellSyncTeammateSelect
                inputId="wellsync-add-prescriber"
                teammates={providerOptions}
                value={addingPrescriber.userId}
                isLoading={teammatesLoading}
                placeholder="Select an AR provider…"
                variant="provider"
                onChange={(v) => {
                  const prov = providerOptions.find(
                    (x) => String(x.id) === String(v),
                  );
                  setAddingPrescriber((p) => ({
                    ...p,
                    userId: v,
                    npi: prov?.npi || p.npi,
                  }));
                }}
              />
              <input
                className={styles.input}
                placeholder="10-digit NPI"
                value={addingPrescriber.npi}
                onChange={(e) =>
                  setAddingPrescriber((p) => ({
                    ...p,
                    npi: e.target.value,
                  }))
                }
              />
              <div className={styles.deaSubhead}>
                <span className={styles.deaSubheadLabel}>
                  DEA registrations
                </span>
                <span className={styles.deaSubheadHint}>
                  (required only for controlled substances)
                </span>
              </div>
              {(addingPrescriber.deaRegs || []).map((dea, j) => (
                <div key={j} className={styles.deaRow}>
                  <input
                    className={styles.input}
                    placeholder="DEA number (e.g., AC1234567)"
                    value={dea.number}
                    onChange={(e) => {
                      const v = e.target.value;
                      setAddingPrescriber((p) => {
                        const deaRegs = [...(p.deaRegs || [])];
                        deaRegs[j] = { ...deaRegs[j], number: v };
                        return { ...p, deaRegs };
                      });
                    }}
                  />
                  <input
                    className={styles.input}
                    placeholder="State (e.g., TX)"
                    maxLength={2}
                    value={dea.state}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setAddingPrescriber((p) => {
                        const deaRegs = [...(p.deaRegs || [])];
                        deaRegs[j] = { ...deaRegs[j], state: v };
                        return { ...p, deaRegs };
                      });
                    }}
                  />
                  <button
                    type="button"
                    className={styles.deaRm}
                    onClick={() =>
                      setAddingPrescriber((p) => ({
                        ...p,
                        deaRegs: p.deaRegs.filter((_, k) => k !== j),
                      }))
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.addLink}
                onClick={() =>
                  setAddingPrescriber((p) => ({
                    ...p,
                    deaRegs: [...(p.deaRegs || []), { number: "", state: "" }],
                  }))
                }
              >
                + Add DEA
              </button>
              <div className={styles.modalFoot}>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => setAddPrescriberConfigId(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={saving}
                  onClick={() => void savePrescriber()}
                >
                  Save Prescriber
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </Box>
    </LayoutSettings>
  );
}

export default WellSyncOnboarding;
